import { chunk, compact, uniq, uniqBy } from 'lodash'
import { action, computed, makeObservable, observable, runInAction } from 'mobx'
import moment from 'moment'
import type { SetStateAction } from 'react'
import { isExcludedCall, isNewPatientCall, isScheduledCall } from 'app/components/ai-agent-voice/useAiAgentVoice'
import { classifyCallFailures } from 'app/components/ai-agent-voice-ops/callDataDebug'
import {
  OPS_VOICE_AGENT_CALL_DATA_QUERY,
  OPS_VOICE_AGENT_DAILY_METRICS_QUERY,
  OPS_VOICE_AGENT_LOCATION_METRICS_QUERY,
  type VoiceAgentCallDataRow,
  type VoiceAgentDailyMetricsData,
  type VoiceAgentLocationMetricsData,
} from 'app/components/ai-agent-voice-ops/graphql'
import {
  CALL_FAILURE_TYPES,
  type LocationDailyCalls,
  NOT_REPORTED_REASON,
  SLIPPING_THRESHOLD_HOURS,
  TOOL_FLAGS,
  dippingLocations,
  ratesFor,
  reasonBreakdown,
  slippingRows as buildSlippingRows,
} from 'app/components/ai-agent-voice-ops/voiceAgentOpsMetrics'
import type {
  CallFailureType,
  OpsCall,
  ToolFlag,
  VoiceAgentOpsCounts,
  VoiceAgentOpsLocationRow,
} from 'app/components/ai-agent-voice-ops/voiceAgentOps.types'
import { SortDirection } from 'app/generated/graphql'
import { gqlFetchList, gqlQuery } from 'app/lib/graphqlClient'
import { opsClient } from 'app/lib/opsUrqlClient'
import type { SearchResponse } from 'app/services/search'
import type { ActivityJson, LocationJson, WorkspaceJson } from 'app/types/entities'
import { bg } from 'lib/background'

export type FlagFilter = ToolFlag | `all`
export type CallFailureFilter = CallFailureType | `all`

/** Slipping grid rows: locations with no AI call in the last 48h as of today, or every AI Agent location. */
export type SlippingScope = `slipping` | `all`

type LocationMetrics = VoiceAgentLocationMetricsData[`opsVoiceAgentLocationMetrics`]
type DailyMetrics = VoiceAgentDailyMetricsData[`opsVoiceAgentDailyMetrics`]
type MetricsRow = LocationMetrics[`rows`][number]
/** The count fields shared by a location row and the weighted total. */
type MetricsCounts = LocationMetrics[`total`]
type CompareRow = NonNullable<LocationMetrics[`compareRows`]>[number]

const LOCATION_FIELDS = `id name workspaceId products isAIAgentLive lastAICallDate`

const CALL_FIELDS = `
  id
  orgId
  workspaceId
  locationId
  type
  subType
  typeId
  activityDate
  source
  archived
  contactName
  contactPhone
  nameFromActivity
  phoneFromActivity
  tags
  contacts { id name phone lifecycleStage locationId }
`

const PAGE_LIMIT = 1000

// extractedData is the source of truth for the ElevenLabs data-collection
// values; the top-level columns (toolError, noBookingReason, ...) are deprecated
// and read only as a fallback for rows not yet backfilled. Derived fields keep
// their camelCase registry key; the tool flags and no_booking_reason come
// through under their raw ElevenLabs id (snake_case).
function extracted(row: VoiceAgentCallDataRow | undefined, key: string): string | undefined {
  const value = row?.extractedData?.[key]
  return typeof value === `string` ? value : undefined
}

const DEPRECATED_TO_EXTRACTED_KEY: Record<string, string> = {
  toolError: `tool_error`,
  toolTimeout: `tool_timeout`,
  toolDuplicate: `tool_duplicate`,
  noBookingReason: `no_booking_reason`,
  callSuccessful: `callSuccessful`,
  terminationReason: `terminationReason`,
  errorCode: `errorCode`,
  errorReason: `errorReason`,
}

function toolFlagOn(row: VoiceAgentCallDataRow | undefined, flag: ToolFlag): boolean {
  const fromExtracted = extracted(row, DEPRECATED_TO_EXTRACTED_KEY[flag])
  if (fromExtracted !== undefined) return fromExtracted === `true`
  return row?.[flag] === true
}

function stringField(
  row: VoiceAgentCallDataRow | undefined,
  key: `noBookingReason` | `callSuccessful` | `terminationReason` | `errorReason`,
): string | undefined {
  return extracted(row, DEPRECATED_TO_EXTRACTED_KEY[key]) ?? row?.[key] ?? undefined
}

function errorCodeField(row: VoiceAgentCallDataRow | undefined): string | undefined {
  const fromExtracted = extracted(row, `errorCode`)
  if (fromExtracted !== undefined) return fromExtracted
  return row?.errorCode === null || row?.errorCode === undefined ? undefined : String(row.errorCode)
}

/** Ids per `OR` lookup, well under OpenSearch's 1,024-clause limit. */
const ID_CHUNK_SIZE = 500

/**
 * Call data with a tool flag or anything `classifyCallFailures` reads as a failure.
 * Termination reasons are prose, so the phrases match loosely here and the classifier
 * drops the few extra rows. Each clause matches both the new extractedData.* key and
 * the deprecated top-level column so rows match whichever is populated during the
 * transition.
 */
const FLAGGED_OR_FAILED_TERM = `(${[
  `extractedData.tool_error.keyword:true`,
  `toolError:true`,
  `extractedData.tool_timeout.keyword:true`,
  `toolTimeout:true`,
  `extractedData.tool_duplicate.keyword:true`,
  `toolDuplicate:true`,
  `extractedData.callSuccessful.keyword:failure`,
  `callSuccessful:failure`,
  `_exists_:extractedData.errorCode`,
  `_exists_:errorCode`,
  `_exists_:extractedData.errorReason`,
  `_exists_:errorReason`,
  `terminationReason:"custom_llm generation failed"`,
  `terminationReason:"llm response took too long"`,
  `terminationReason:"client disconnected"`,
  `(terminationReason:"no user message" AND terminationReason:network)`,
  `terminationReason:"maximum duration"`,
  `extractedData.terminationReason:"custom_llm generation failed"`,
  `extractedData.terminationReason:"llm response took too long"`,
  `extractedData.terminationReason:"client disconnected"`,
  `(extractedData.terminationReason:"no user message" AND extractedData.terminationReason:network)`,
  `extractedData.terminationReason:"maximum duration"`,
].join(` OR `)})`

/** New patient calls with none of the tags `isScheduledCall` treats as booked. */
const UNBOOKED_NEW_PATIENT_TERM = `tags.keyword:"New Patient" NOT tags.keyword:("Appointment Scheduled" OR "Appointment Rescheduled" OR "Appointment Confirmation")`

async function fetchAllCallData(search: string): Promise<VoiceAgentCallDataRow[]> {
  const rows: VoiceAgentCallDataRow[] = []
  for (let page = 0; ; page++) {
    const { data } = await gqlQuery(OPS_VOICE_AGENT_CALL_DATA_QUERY, {
      search,
      pagination: { limit: PAGE_LIMIT, page },
      sort: { field: `callDate`, direction: SortDirection.Desc },
    })
    const hits = compact(data?.callData.hits ?? [])
    rows.push(...hits)
    if (hits.length < PAGE_LIMIT) return rows
  }
}

async function fetchAllActivities(search: string): Promise<ActivityJson[]> {
  const activities: ActivityJson[] = []
  for (let page = 0; ; page++) {
    const { hits } = await gqlFetchList<ActivityJson>(
      `activities`,
      { search, pagination: { limit: PAGE_LIMIT, page }, sort: { field: `activityDate`, direction: `desc` } },
      CALL_FIELDS,
    )
    activities.push(...(hits as ActivityJson[]))
    if (hits.length < PAGE_LIMIT) return activities
  }
}

/** Runs `lookup` over `ids` in `field:(…)` chunks and flattens the results. */
async function fetchByIds<T>(ids: string[], field: string, lookup: (term: string) => Promise<T[]>): Promise<T[]> {
  const chunks = chunk(uniq(ids), ID_CHUNK_SIZE)
  const results = await Promise.all(
    chunks.map((idChunk) => lookup(`${field}:(${idChunk.map((id) => `"${id}"`).join(` OR `)})`)),
  )
  return results.flat()
}

/** API counts for one location (or the weighted total) as the UI's count shape. */
function countsFrom(row: MetricsCounts | undefined, compare: CompareRow | undefined): VoiceAgentOpsCounts {
  return {
    aiCalls: row?.aiCalls ?? 0,
    newPatientCalls: row?.newPatientCalls ?? 0,
    appointmentsBooked: row?.appointmentsBooked ?? 0,
    appointmentsCompleted: row?.appointmentsCompleted ?? 0,
    toolErrorCalls: row?.toolErrorCalls ?? 0,
    toolTimeoutCalls: row?.toolTimeoutCalls ?? 0,
    toolDuplicateCalls: row?.toolDuplicateCalls ?? 0,
    failedCalls: row?.failedCalls ?? 0,
    llmFailedCalls: row?.llmFailedCalls ?? 0,
    llmSlowCalls: row?.llmSlowCalls ?? 0,
    disconnectedCalls: row?.disconnectedCalls ?? 0,
    maxDurationCalls: row?.maxDurationCalls ?? 0,
    errorCalls: row?.errorCalls ?? 0,
    unsuccessfulCalls: row?.unsuccessfulCalls ?? 0,
    gptFailedCalls: row?.gptFailedCalls ?? 0,
    elevenLabsFailedCalls: row?.elevenLabsFailedCalls ?? 0,
    previousNewPatientCalls: compare?.newPatientCalls ?? 0,
    previousAppointmentsBooked: compare?.appointmentsBooked ?? 0,
  }
}

/**
 * State for the super-user Ops tab on the AI Voice Agent page: the scope it looks at,
 * the locations, metrics and AI calls it loaded, the list filters, and every table,
 * chart and card series derived from them.
 *
 * Cards, Location Breakdown, Dipping Schedule Rates, both charts and the Slipping
 * grid come from `opsVoiceAgentLocationMetrics` and `opsVoiceAgentDailyMetrics` on
 * the ops schema. The three call lists load only the calls they show, filtered on the
 * server through `callData` and `activities` searches, and join the two on call id.
 */
export class VoiceAgentOpsStore {
  /** Scope, set from the page's `?fromDate` / `?toDate` / `?locationIds`. */
  fromDate = moment().startOf(`month`).format(`YYYY-MM-DD`)
  toDate = moment().endOf(`month`).format(`YYYY-MM-DD`)
  locationIds: string[] = []
  /** A workspace is selected, so the org-level Workspace column and its names are not needed. */
  hasWorkspace = false
  /**
   * The session's org. `callData` scopes a super user with no workspace to every org
   * (it serves the ops search tool), so the store narrows it here; without this the org
   * view joins this org's calls against a page of other customers' call data.
   */
  orgId: string | undefined = undefined

  locations: LocationJson[] | undefined = undefined
  workspaceNames: Record<string, string> = {}
  metrics: LocationMetrics | undefined = undefined
  dailyMetrics: DailyMetrics | undefined = undefined
  calls: SearchResponse<ActivityJson> | undefined = undefined
  callDataRows: VoiceAgentCallDataRow[] | undefined = undefined

  flagFilter: FlagFilter = `all`
  failureFilter: CallFailureFilter = `all`
  reasonFilter = `all`
  slippingScope: SlippingScope = `slipping`

  /** Bumped per request; a response from an older scope must not overwrite a newer one. */
  private locationsVersion = 0
  private metricsVersion = 0
  private dailyVersion = 0
  private callsVersion = 0

  constructor() {
    // makeObservable (not makeAutoObservable): excludes private version counters
    // and the callDataByCallId cache from observability and uses observable.ref
    // for large payloads, neither of which makeAutoObservable can express.
    makeObservable<this, `locationsVersion` | `metricsVersion` | `dailyVersion` | `callsVersion` | `callDataByCallId`>(
      this,
      {
        fromDate: observable,
        toDate: observable,
        locationIds: observable,
        hasWorkspace: observable,
        orgId: observable,
        locations: observable.ref,
        workspaceNames: observable.ref,
        metrics: observable.ref,
        dailyMetrics: observable.ref,
        calls: observable.ref,
        callDataRows: observable.ref,
        flagFilter: observable,
        failureFilter: observable,
        reasonFilter: observable,
        slippingScope: observable,
        locationsVersion: false,
        metricsVersion: false,
        dailyVersion: false,
        callsVersion: false,
        previousFromDate: computed,
        previousToDate: computed,
        callDataSearch: computed,
        activitySearch: computed,
        isLoading: computed,
        isSlippingLoading: computed,
        scopedLocations: computed,
        daily: computed,
        locationRows: computed,
        summary: computed,
        dippingRows: computed,
        allProductLocations: computed,
        locationDailyCalls: computed,
        allSlippingRows: computed,
        slippingRows: computed,
        callDataByCallId: computed,
        opsCalls: computed,
        toolFailureCalls: computed,
        flagCounts: computed,
        failedCalls: computed,
        callFailures: computed,
        failureCounts: computed,
        visibleCallFailures: computed,
        unbookedCalls: computed,
        unbookedCallCount: computed,
        reasonRows: computed,
        activeReasonFilter: computed,
        visibleUnbookedCalls: computed,
        setScope: action.bound,
        setFlagFilter: action.bound,
        setFailureFilter: action.bound,
        setReasonFilter: action.bound,
        setSlippingScope: action.bound,
        setCalls: action.bound,
        loadLocations: action.bound,
        loadMetrics: action.bound,
        loadDailyMetrics: action.bound,
        loadCalls: action.bound,
        reset: action.bound,
      },
    )
  }

  setScope(fromDate: string, toDate: string, locationIds: string[], hasWorkspace: boolean, orgId?: string) {
    this.fromDate = fromDate
    this.toDate = toDate
    this.locationIds = locationIds
    this.hasWorkspace = hasWorkspace
    this.orgId = orgId
  }

  setFlagFilter(filter: FlagFilter) {
    this.flagFilter = filter
  }

  setFailureFilter(filter: CallFailureFilter) {
    this.failureFilter = filter
  }

  setReasonFilter(reason: string) {
    this.reasonFilter = reason
  }

  setSlippingScope(scope: SlippingScope) {
    this.slippingScope = scope
  }

  /** Matches React's setter shape, so `useActivityReload` can patch one reloaded call into the list. */
  setCalls(update: SetStateAction<SearchResponse<ActivityJson> | undefined>) {
    this.calls = typeof update === `function` ? update(this.calls) : update
  }

  /** AI Agent locations in scope: entitled to the product, or already took AI calls. */
  loadLocations() {
    const version = ++this.locationsVersion
    bg(async () => {
      const [locationList, workspaceList] = await Promise.all([
        gqlFetchList<LocationJson>(`locations`, { pagination: { limit: 1000 } }, LOCATION_FIELDS),
        this.hasWorkspace
          ? undefined
          : gqlFetchList<WorkspaceJson>(`workspaces`, { pagination: { limit: 1000 } }, `id name`),
      ])
      if (version !== this.locationsVersion) return
      runInAction(() => {
        this.locations = locationList.hits.filter(
          (location) => location.products?.includes(`AI Agent`) || location.lastAICallDate,
        )
        this.workspaceNames = Object.fromEntries((workspaceList?.hits ?? []).map((ws) => [ws.id!, ws.name ?? ``]))
      })
    })
  }

  /**
   * Counts per location for the cards, Location Breakdown and Dipping Schedule Rates.
   * `compareDateRange` returns the previous period in the same request, so a dip needs
   * no second round trip.
   */
  loadMetrics() {
    const version = ++this.metricsVersion
    this.metrics = undefined
    bg(async () => {
      const { data } = await gqlQuery(
        OPS_VOICE_AGENT_LOCATION_METRICS_QUERY,
        {
          input: {
            dateRange: { fromDate: this.fromDate, toDate: this.toDate },
            compareDateRange: { fromDate: this.previousFromDate, toDate: this.previousToDate },
            locationIds: this.locationIds.length ? this.locationIds : undefined,
          },
        },
        { client: opsClient },
      )
      if (version !== this.metricsVersion) return
      runInAction(() => {
        this.metrics = data?.opsVoiceAgentLocationMetrics
      })
    })
  }

  /** Per-day counts for both charts, and per-location daily calls for the Slipping grid. */
  loadDailyMetrics() {
    const version = ++this.dailyVersion
    this.dailyMetrics = undefined
    bg(async () => {
      const { data } = await gqlQuery(
        OPS_VOICE_AGENT_DAILY_METRICS_QUERY,
        {
          input: {
            dateRange: { fromDate: this.fromDate, toDate: this.toDate },
            locationIds: this.locationIds.length ? this.locationIds : undefined,
            // No timezone: callDate/activityDate are stored as clinic-local
            // wall-clock time, so sending one shifts the days a second time
            // (TRK-5796). The API buckets on the stored local day.
          },
        },
        { client: opsClient },
      )
      if (version !== this.dailyVersion) return
      runInAction(() => {
        this.dailyMetrics = data?.opsVoiceAgentDailyMetrics
      })
    })
  }

  /**
   * Every call the three lists show, filtered server-side so no list is cut short: call
   * data with a tool flag or failure, and unbooked new patient activities. Each side is
   * then joined to its other half by call id (`CallData.callId` = `Activity.typeId`).
   */
  loadCalls() {
    const version = ++this.callsVersion
    this.calls = undefined
    this.callDataRows = undefined
    const { activitySearch, callDataSearch } = this
    bg(async () => {
      const [flaggedCallData, unbookedActivities] = await Promise.all([
        fetchAllCallData(`${callDataSearch} ${FLAGGED_OR_FAILED_TERM}`),
        fetchAllActivities(`${activitySearch} ${UNBOOKED_NEW_PATIENT_TERM}`),
      ])
      const [flaggedActivities, unbookedCallData] = await Promise.all([
        fetchByIds(
          flaggedCallData.map((row) => row.callId),
          `typeId`,
          (term) => fetchAllActivities(`${activitySearch} ${term}`),
        ),
        fetchByIds(compact(unbookedActivities.map((activity) => activity.typeId)), `callId`, (term) =>
          fetchAllCallData(`${callDataSearch} ${term}`),
        ),
      ])
      if (version !== this.callsVersion) return
      const hits = uniqBy([...flaggedActivities, ...unbookedActivities], `id`).sort((a, b) =>
        (b.activityDate ?? ``).localeCompare(a.activityDate ?? ``),
      )
      runInAction(() => {
        this.calls = { hits, total: hits.length, page: 0 }
        this.callDataRows = uniqBy([...flaggedCallData, ...unbookedCallData], `id`)
      })
    })
  }

  reset() {
    this.locations = undefined
    this.workspaceNames = {}
    this.metrics = undefined
    this.dailyMetrics = undefined
    this.calls = undefined
    this.callDataRows = undefined
    this.flagFilter = `all`
    this.failureFilter = `all`
    this.reasonFilter = `all`
    this.slippingScope = `slipping`
  }

  /** The dip comparison's previous period: the same number of days, ending the day before the range. */
  get previousFromDate(): string {
    const rangeDays = moment.utc(this.toDate).diff(moment.utc(this.fromDate), `days`) + 1
    return moment.utc(this.fromDate).subtract(rangeDays, `days`).format(`YYYY-MM-DD`)
  }

  get previousToDate(): string {
    return moment.utc(this.fromDate).subtract(1, `day`).format(`YYYY-MM-DD`)
  }

  /** The `callData` search for the call lists, always held to the page's org (see `orgId`). */
  get callDataSearch(): string {
    return compact([
      this.orgId ? `orgId:"${this.orgId}"` : undefined,
      `callDate:[${this.fromDate} TO ${this.toDate}]`,
      this.locationIds.length ? `locationId:(${this.locationIds.join(` OR `)})` : undefined,
    ]).join(` `)
  }

  /** This range's AI call activities, test calls excluded. */
  get activitySearch(): string {
    return compact([
      `type:Call`,
      `tags:AI`,
      `NOT tags.keyword:"Test Call"`,
      `activityDate:[${this.fromDate} TO ${this.toDate}]`,
      this.locationIds.length ? `locationId:(${this.locationIds.join(` OR `)})` : undefined,
    ]).join(` `)
  }

  get isLoading(): boolean {
    return !this.locationRows
  }

  get isSlippingLoading(): boolean {
    return !this.allSlippingRows
  }

  get scopedLocations(): LocationJson[] | undefined {
    return this.locations?.filter((location) => !this.locationIds.length || this.locationIds.includes(location.id!))
  }

  get daily() {
    return this.dailyMetrics?.daily ?? []
  }

  /**
   * One row per location in scope, including locations with no calls in range, so the
   * breakdown lists every AI Agent location rather than only the busy ones.
   */
  get locationRows(): VoiceAgentOpsLocationRow[] | undefined {
    if (!this.scopedLocations || !this.metrics) return undefined
    const rowByLocation = new Map(this.metrics.rows.map((row) => [row.locationId, row]))
    const compareByLocation = new Map((this.metrics.compareRows ?? []).map((row) => [row.locationId, row]))
    return this.scopedLocations.map((location) => ({
      ...countsFrom(rowByLocation.get(location.id!), compareByLocation.get(location.id!)),
      locationId: location.id!,
      locationName: location.name ?? ``,
      workspaceId: location.workspaceId ?? undefined,
      workspaceName: location.workspaceId ? this.workspaceNames[location.workspaceId] : undefined,
      isAIAgentLive: !!location.isAIAgentLive,
      lastAICallDate: location.lastAICallDate ?? undefined,
    }))
  }

  /** The API's weighted total, not a sum of the rows above, so it holds at org scale. */
  get summary() {
    const counts = countsFrom(this.metrics?.total, this.metrics?.compareTotal ?? undefined)
    return { ...counts, ...ratesFor(counts) }
  }

  get dippingRows(): VoiceAgentOpsLocationRow[] | undefined {
    return this.locationRows && dippingLocations(this.locationRows)
  }

  /** The slipping grid covers every location with the product, live or not (plan D17). */
  get allProductLocations(): LocationJson[] | undefined {
    return this.locations?.filter((location) => location.products?.includes(`AI Agent`))
  }

  get locationDailyCalls(): LocationDailyCalls[] | undefined {
    return this.dailyMetrics?.locations.map((location) => ({
      locationId: location.locationId,
      lastCallBeforeRange: location.lastCallBeforeRange ?? undefined,
      callsByDate: Object.fromEntries(location.days.map((day) => [day.date, day.aiCalls])),
    }))
  }

  get allSlippingRows() {
    if (!this.allProductLocations || !this.locationDailyCalls) return undefined
    const inScope = this.allProductLocations.filter(
      (location) => !this.locationIds.length || this.locationIds.includes(location.id!),
    )
    return buildSlippingRows(
      inScope,
      this.locationDailyCalls,
      this.fromDate,
      this.toDate,
      SLIPPING_THRESHOLD_HOURS,
      moment(),
    )
  }

  get slippingRows() {
    return this.slippingScope === `all` ? this.allSlippingRows : this.allSlippingRows?.filter((row) => row.isSlipping)
  }

  /** `CallData.callId` is the call behind an activity, which the activity holds as `typeId`. */
  private get callDataByCallId(): Map<string, VoiceAgentCallDataRow> {
    return new Map(compact(this.callDataRows ?? []).map((row) => [row.callId, row]))
  }

  get opsCalls(): OpsCall[] | undefined {
    if (!this.calls || !this.callDataRows) return undefined
    return this.calls.hits.map((activity) => {
      const row = activity.typeId ? this.callDataByCallId.get(activity.typeId) : undefined
      return {
        ...activity,
        durationSeconds: row?.duration ?? 0,
        toolFlags: TOOL_FLAGS.filter((flag) => toolFlagOn(row, flag)),
        failureTypes: row
          ? classifyCallFailures({
              terminationReason: stringField(row, `terminationReason`),
              errorCode: errorCodeField(row),
              errorReason: stringField(row, `errorReason`),
              callSuccessful: stringField(row, `callSuccessful`),
            })
          : [],
        isNewPatient: isNewPatientCall(activity) && !isExcludedCall(activity),
        isBooked: isScheduledCall(activity),
        noBookingReason: stringField(row, `noBookingReason`) ?? null,
      }
    })
  }

  get toolFailureCalls() {
    return this.opsCalls?.filter((call) => call.toolFlags.length > 0)
  }

  get flagCounts(): Record<FlagFilter, number> {
    return Object.fromEntries([
      [`all`, this.toolFailureCalls?.length ?? 0],
      ...TOOL_FLAGS.map((flag) => [
        flag,
        this.toolFailureCalls?.filter((call) => call.toolFlags.includes(flag)).length ?? 0,
      ]),
    ]) as Record<FlagFilter, number>
  }

  get failedCalls() {
    return this.flagFilter === `all`
      ? this.toolFailureCalls
      : this.toolFailureCalls?.filter((call) => call.toolFlags.includes(this.flagFilter as ToolFlag))
  }

  get callFailures() {
    return this.opsCalls?.filter((call) => call.failureTypes.length > 0)
  }

  get failureCounts(): Record<CallFailureFilter, number> {
    return Object.fromEntries([
      [`all`, this.callFailures?.length ?? 0],
      ...CALL_FAILURE_TYPES.map((type) => [
        type,
        this.callFailures?.filter((call) => call.failureTypes.includes(type)).length ?? 0,
      ]),
    ]) as Record<CallFailureFilter, number>
  }

  get visibleCallFailures() {
    return this.failureFilter === `all`
      ? this.callFailures
      : this.callFailures?.filter((call) => call.failureTypes.includes(this.failureFilter as CallFailureType))
  }

  /** Unbooked means new patient calls that did not book (plan D43). */
  get unbookedCalls() {
    return this.opsCalls?.filter((call) => call.isNewPatient && !call.isBooked)
  }

  get unbookedCallCount(): number {
    return this.unbookedCalls?.length ?? 0
  }

  get reasonRows() {
    return this.unbookedCalls && reasonBreakdown(this.unbookedCalls)
  }

  /** A reason that is gone from the breakdown (range or locations changed) falls back to All reasons. */
  get activeReasonFilter(): string {
    return this.reasonRows?.some((row) => row.reason === this.reasonFilter) ? this.reasonFilter : `all`
  }

  get visibleUnbookedCalls() {
    return this.activeReasonFilter === `all`
      ? this.unbookedCalls
      : this.unbookedCalls?.filter((call) => (call.noBookingReason || NOT_REPORTED_REASON) === this.activeReasonFilter)
  }
}
